import { useCallback, useEffect, useRef, useState } from "react";

// Chime morbido e breve (Web Audio, nessun file esterno).
export function useChime() {
  const ctxRef = useRef(null);
  const [enabled, setEnabled] = useState(true);
  const [needsUnlock, setNeedsUnlock] = useState(false);

  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctxRef.current = new Ctx();
    }
    return ctxRef.current;
  }, []);

  const unlock = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;
    ctx.resume().then(() => setNeedsUnlock(false)).catch(() => {});
  }, [getCtx]);

  useEffect(() => {
    const ctx = getCtx();
    if (ctx && ctx.state === "suspended") setNeedsUnlock(true);
    const onInteract = () => unlock();
    window.addEventListener("pointerdown", onInteract, { once: true });
    window.addEventListener("keydown", onInteract, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
  }, [getCtx, unlock]);

  const play = useCallback(() => {
    if (!enabled) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      setNeedsUnlock(true);
      return;
    }
    const t0 = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(1.0, t0 + 0.012);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.4);
    master.connect(ctx.destination);

    // "Dong" deciso: fondamentale piena + armoniche brillanti, attacco secco
    const voci = [
      { freq: 392, type: "triangle", gain: 0.95, delay: 0, decay: 1.25 },
      { freq: 784, type: "sine", gain: 0.7, delay: 0, decay: 0.9 },
      { freq: 1176, type: "sine", gain: 0.32, delay: 0.055, decay: 0.6 },
    ];
    voci.forEach(({ freq, type, gain: g, delay, decay }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, t0 + delay);
      gain.gain.setValueAtTime(0.0001, t0 + delay);
      gain.gain.exponentialRampToValueAtTime(g, t0 + delay + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + delay + decay);
      osc.connect(gain).connect(master);
      osc.start(t0 + delay);
      osc.stop(t0 + delay + decay + 0.05);
    });
  }, [enabled, getCtx]);

  return { play, enabled, setEnabled, needsUnlock, unlock };
}
